// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import emailPreviewApi from '../../api/emailPreviewApi';
import type { EmailPreviewResponse } from '../../types/emailPreview';
import type { Policy } from '../../types/policy';
import type { Client } from '../../types/client';
import type { Relationship } from '../../types/relationship';
import { EmailComposerPanel } from '../email/EmailComposerPanel';
import { RecipientInfoCard } from '../email/RecipientInfoCard';
import { PoliciesCard } from '../email/PoliciesCard';
import { PolicyList } from '../PolicyList';
import { useCoAWizard } from './CoAWizardContext';

export type CoAEmailCraftingStepProps = Readonly<{
  /** Called when the advisor commits the wizard (createCoA happy path) */
  onSubmit: () => void;
  /** Called when the advisor cancels and wants to return to the relationship page */
  onCancel: () => void;
  /** All policies loaded for the relationship (used to render the selected rows). */
  policies: Policy[];
  /** Primary client — drives the header (name, phone, email) and Recipients field. */
  primaryClient: Client | null;
  /** Relationship being targeted, used as a header fallback. */
  relationship: Relationship;
  /**
   * Profile ID (same value as {@code relationship.id}) used to fetch the
   * server-owned default subject + body for the CoA client invitation via
   * {@code POST /api/email-preview}. Required because the wizard shows the
   * default BEFORE the CoA entity is persisted, so the preview endpoint
   * resolves recipients from the profile rather than a CoA row.
   */
  profileId: string;
}>;

/**
 * Step 2 of the CoA wizard. Renders the shared email composer with the
 * server-owned default subject + body fetched from
 * {@code POST /api/email-preview} on mount. Previously this component
 * carried a hardcoded {@code buildDefaultCoATemplate} — that duplication is
 * gone; the canonical default now lives solely in
 * {@code EmailContentCatalog} + {@code EmailPreviewService} on the server.
 */
export function CoAEmailCraftingStep({
  onSubmit,
  onCancel,
  policies,
  primaryClient,
  relationship,
  profileId,
}: CoAEmailCraftingStepProps) {
  const { state, updateEmailContent, resetEmailToDefault } = useCoAWizard();

  const primaryClientName = primaryClient
    ? `${primaryClient.firstName} ${primaryClient.lastName}`.trim()
    : relationship.primaryClientName ?? relationship.relationshipName ?? null;

  const [preview, setPreview] = useState<EmailPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    emailPreviewApi
      .preview({ kind: 'COA_CLIENT_INVITATION', profileId })
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(
            err instanceof Error ? err.message : 'Failed to load email defaults'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const selectedPolicies = useMemo(
    () => policies.filter((p) => state.selectedPolicyIds.includes(p.id)),
    [policies, state.selectedPolicyIds]
  );

  const defaultSubject = preview?.subject ?? '';
  const defaultBody = preview?.editableBodyHtml ?? '';

  const subject = state.isEmailModified ? state.customSubject : defaultSubject;
  const body = state.isEmailModified ? state.customBody : defaultBody;

  const handleSubjectChange = useCallback(
    (next: string) => {
      updateEmailContent(next, body);
    },
    [updateEmailContent, body]
  );

  const handleBodyChange = useCallback(
    (next: string) => {
      updateEmailContent(subject, next);
    },
    [updateEmailContent, subject]
  );

  // Wait for the server-owned defaults before mounting the composer.
  // Mounting the TiptapEditor with an empty {@code content} causes its
  // initial {@code setContent('')} to emit an onUpdate with the empty
  // placeholder HTML, which flips {@code state.isEmailModified} to true
  // and freezes the composer on empty values — the server defaults would
  // then be ignored when the fetch resolves. Guarding on {@code preview}
  // keeps the composer mounted with the real default from the first paint.
  if (!preview) {
    return (
      <div className="w-full py-8 px-6">
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-grey-02">
            {previewError ?? 'Loading email template...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <EmailComposerPanel
      subject={subject}
      onSubjectChange={handleSubjectChange}
      subjectPlaceholder={defaultSubject}
      recipients={primaryClient?.email ?? ''}
      body={body}
      onBodyChange={handleBodyChange}
      isModified={state.isEmailModified}
      onReset={resetEmailToDefault}
      onCancel={onCancel}
      onSend={onSubmit}
      sendLabel="Send Email"
      sending={state.isSubmitting}
      error={state.submitError ?? previewError}
    >
      <RecipientInfoCard
        title={primaryClientName || 'Change of Agency'}
        email={primaryClient?.email}
        phone={primaryClient?.phone}
      />

      {selectedPolicies.length > 0 && (
        <PoliciesCard
          title={`Policies for Change of Agency (${selectedPolicies.length})`}
          description="These are the policies the client will sign a Change of Agency for."
        >
          <PolicyList
            policies={selectedPolicies}
            loading={false}
            error={null}
            onEdit={() => {}}
            onDelete={() => {}}
            onManageRelationships={() => {}}
            hideActionColumn
          />
        </PoliciesCard>
      )}
    </EmailComposerPanel>
  );
}

export default CoAEmailCraftingStep;
